import React, { useCallback } from 'react';
import { useObservable } from 'react-use-observable';

import { ShoppingCartOutlined } from '@ant-design/icons';
import { Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { formatDate } from 'helpers/date';
import Truncate from 'v2/components/Shared/TruncateText';
import WhatsAppLink from 'v2/components/Shared/WhatsAppLink';
import leadsService from 'v2/services/leads/list';
import tableService from 'v2/services/table';

const columns: ColumnsType<any> = [
  {
    title: 'Data',
    dataIndex: 'created_at',
    width: 120,
    render: (text: string) => (
      <div>
        <span className='block text-xs'>{text ? formatDate(text, 'dd/MM/yyyy') : '-'}</span>
        <span className='block text-xs'>{text ? formatDate(text, 'HH:mm') : '-'}</span>
      </div>
    )
  },
  {
    title: 'Nome',
    render: (_: any, record: any) => <span className='text-xs'>{record.customer?.name ?? '-'}</span>
  },
  {
    title: 'E-mail',
    dataIndex: 'email',
    width: 250,
    render: (text: string) => (
      <span className='text-xs'>
        <Truncate count={30} final='...'>
          {text}
        </Truncate>
      </span>
    )
  },
  {
    title: 'Telefone',
    width: 120,
    render: (_: any, record: any) =>
      record?.customer?.cellphone ? <WhatsAppLink phone={record.customer.cellphone} /> : record.customer?.telephone
  },
  {
    title: 'Campanha',
    width: 140,
    render: (_: any, record: any) => record.campaign?.key ?? '-'
  },
  {
    title: 'Produto',
    render: (_: any, record: any) => (
      <div>
        <span className='block text-xs font-semibold'>
          <Truncate count={30} final='...'>
            {record?.product?.title ?? '-'}
          </Truncate>
        </span>
        <span className='text-xs'>{record?.product?.id ? `(${record?.product?.id})` : '-'}</span>
      </div>
    )
  },
  {
    title: 'Afiliado',
    width: 175,
    render: (_: any, record: any) => record.affiliate?.name ?? '-'
  },
  {
    title: 'Fatura',
    width: 100,
    render: (_: any, record: any) => (
      <>
        {record.abandonment && !record.invoice_id && (
          <Tooltip placement='left' title='Abandono de carrinho'>
            <span className='text-xl'>
              <ShoppingCartOutlined />
            </span>
          </Tooltip>
        )}
        {!record.abandonment && record.invoice_id && <span>{record.invoice_id}</span>}
      </>
    )
  }
];

const List = () => {
  useObservable(() => leadsService.load(), []);

  const [isLoading] = useObservable(() => tableService.isLoading(), []);
  const [leads] = useObservable(() => leadsService.getLeads(), []);
  const [pagination] = useObservable(() => leadsService.getPaginationResponse(), []);

  const onChangePerPage = useCallback(
    (perPage: number) => {
      leadsService.setPagination({ ...pagination, per_page: perPage, current_page: 1 });
    },
    [pagination]
  );

  const onChangePage = useCallback(
    (page: number) => {
      leadsService.setPagination({ ...pagination, current_page: page });
    },
    [pagination]
  );

  return (
    <div>
      <Table
        id='leads_table'
        dataSource={leads || []}
        scroll={{ x: 1000 }}
        size='small'
        columns={columns}
        rowKey='id'
        loading={isLoading}
        pagination={
          isLoading
            ? false
            : {
                current: pagination?.current_page || 1,
                pageSize: pagination?.per_page || 10,
                total: pagination?.total_items || 0,
                pageSizeOptions: [10, 20, 50, 100, 200, 500],
                showSizeChanger: true,
                onChange: page => onChangePage(page),
                onShowSizeChange: (_, size) => onChangePerPage(size)
              }
        }
      />
    </div>
  );
};
export default List;
